import { motion } from "framer-motion";
import { Car } from "lucide-react";

// Logo JYVIS avec URL
const JYVIS_LOGO = "/logo.svg";

// Logo TheGoodDeal.fr - Partenaire
const THEGOODDEAL_LOGO = "/images/thegooddeal.jpg";

// Thèmes de couleurs pour l'auréole animée - Bleu violet foncé professionnel
const colorThemes = {
  // Bleu violet foncé avec contraste pour être visible
  default: "conic-gradient(from 0deg, #1E3A8A, #3730A3, #4C1D95, #6D28D9, #1E3A8A)",
  particuliers: "conic-gradient(from 0deg, #1E3A8A, #3730A3, #4C1D95, #6D28D9, #1E3A8A)",
  professionnels: "conic-gradient(from 0deg, #1E3A8A, #3730A3, #4C1D95, #6D28D9, #1E3A8A)",
  contact: "conic-gradient(from 0deg, #1E3A8A, #3730A3, #4C1D95, #6D28D9, #1E3A8A)",
};

// Composant pour le logo TheGoodDeal - Proportionnel à JYVIS avec voitures qui tournent autour
function AnimatedTheGoodDealLogo({ size = "md", className = "" }) {
  const sizes = {
    sm: { logoH: "h-8", orbit: 28 },
    md: { logoH: "h-10", orbit: 34 },
    lg: { logoH: "h-12", orbit: 40 },
  };

  const s = sizes[size] || sizes.md;

  return (
    <a 
      href="https://thegooddeal.fr" 
      target="_blank" 
      rel="noopener noreferrer"
      className={`relative flex items-center justify-center hover:opacity-90 transition-opacity ${className}`}
      title="TheGoodDeal.fr - Des bonnes affaires, en toute sérénité"
      style={{ width: s.orbit * 2 + 20, height: s.orbit * 2 }}
    >
      {/* Logo TheGoodDeal au centre - proportionnel à JYVIS */}
      <img 
        src={THEGOODDEAL_LOGO} 
        alt="TheGoodDeal.fr" 
        className={`${s.logoH} w-auto object-contain rounded-md relative z-10`}
      />

      {/* Voiture 1 - Orange - Orbite autour du logo */}
      <motion.div
        className="absolute"
        style={{ 
          width: s.orbit * 2,
          height: s.orbit * 2,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Car 
            className="w-4 h-4 text-[#F97316]"
            style={{ 
              transform: "rotate(90deg)",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))"
            }} 
          />
        </div>
      </motion.div>

      {/* Voiture 2 - Bleue - Orbite en sens inverse */}
      <motion.div
        className="absolute"
        style={{ 
          width: s.orbit * 2,
          height: s.orbit * 2,
        }}
        animate={{ rotate: -360 }}
        transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
      >
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <Car 
            className="w-4 h-4 text-[#3B82F6]"
            style={{ 
              transform: "rotate(-90deg)",
              filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))"
            }} 
          />
        </div>
      </motion.div>
    </a>
  );
}

// Logo animé pour les headers avec thème de couleur personnalisable
export function HeaderLogo({ theme = "default", size = "md", showName = true, dark = true, className = "", showPartner = true }) {
  const sizes = {
    sm: { logo: "h-8", text: "text-lg", partner: "sm" },
    md: { logo: "h-10", text: "text-xl", partner: "md" },
    lg: { logo: "h-12", text: "text-2xl", partner: "lg" },
  };

  const s = sizes[size] || sizes.md;
  const gradient = colorThemes[theme] || colorThemes.default;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Logo JYVIS avec anneau animé */}
      <div className="relative">
        <motion.div
          className="absolute -inset-1 rounded-xl"
          style={{ background: gradient }}
          animate={{ rotate: 360 }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        />
        <div className="relative rounded-lg p-1">
          <img src={JYVIS_LOGO} alt="JYVIS" className={`${s.logo} w-auto object-contain`} />
        </div>
      </div>
      {showName && (
        <span className={`font-bold ${s.text} ${dark ? "text-white" : "text-[#102692]"}`}>
          JYVIS
        </span>
      )}
      
      {/* Séparateur + Logo TheGoodDeal avec voitures animées */}
      {showPartner && (
        <>
          <div className={`h-8 w-px ${dark ? "bg-white/30" : "bg-gray-300"} mx-1`} />
          <AnimatedTheGoodDealLogo size={s.partner} />
        </>
      )}
    </div>
  );
}

// Export du composant TheGoodDeal pour utilisation individuelle
export { AnimatedTheGoodDealLogo };

export default function AnimatedLogo({ size = "md", showName = true, className = "" }) {
  const sizes = {
    sm: { logo: "h-8 w-8", ring: "w-12 h-12", text: "text-lg" },
    md: { logo: "h-12 w-12", ring: "w-16 h-16", text: "text-xl" },
    lg: { logo: "h-16 w-16", ring: "w-20 h-20", text: "text-2xl" },
    xl: { logo: "h-20 w-20", ring: "w-24 h-24", text: "text-3xl" },
  };

  const s = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {/* Logo avec anneau violet animé */}
      <div className="relative">
        {/* Anneau violet animé qui tourne */}
        <motion.div
          className={`absolute inset-0 ${s.ring} -m-2 rounded-full`}
          style={{
            background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #8B5CF6)",
            opacity: 0.8,
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        
        {/* Anneau intérieur blanc pour créer l'effet de bordure */}
        <div className={`absolute inset-0 ${s.ring} -m-2 rounded-full bg-white scale-[0.85]`} />
        
        {/* Logo */}
        <img 
          src={JYVIS_LOGO} 
          alt="JYVIS Assurances" 
          className={`${s.logo} object-contain relative z-10`}
        />
      </div>
      
      {/* Nom JYVIS */}
      {showName && (
        <div className="flex flex-col">
          <span className={`font-bold ${s.text} text-[#102692]`}>JYVIS</span>
          <span className="text-xs text-gray-500 -mt-1">Assurances</span>
        </div>
      )}
    </div>
  );
}

// Version simple sans animation pour les headers
export function SimpleLogo({ size = "md", showName = true, className = "", dark = false }) {
  const sizes = {
    sm: { logo: "h-8", text: "text-lg" },
    md: { logo: "h-10", text: "text-xl" },
    lg: { logo: "h-12", text: "text-2xl" },
  };

  const s = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="relative">
        {/* Bordure violette statique */}
        <div 
          className="absolute inset-0 -m-1 rounded-lg"
          style={{
            background: "linear-gradient(135deg, #8B5CF6, #A855F7, #D946EF)",
            padding: "2px",
          }}
        >
          <div className="w-full h-full bg-white rounded-lg" />
        </div>
        <img 
          src={JYVIS_LOGO} 
          alt="JYVIS" 
          className={`${s.logo} object-contain relative z-10`}
        />
      </div>
      {showName && (
        <span className={`font-bold ${s.text} ${dark ? "text-white" : "text-[#102692]"}`}>
          JYVIS
        </span>
      )}
    </div>
  );
}

// Logo avec anneau violet qui pulse
export function PulsingLogo({ size = "lg", className = "" }) {
  const sizes = {
    md: { logo: "h-12 w-12", container: "w-16 h-16" },
    lg: { logo: "h-16 w-16", container: "w-20 h-20" },
    xl: { logo: "h-24 w-24", container: "w-28 h-28" },
  };

  const s = sizes[size] || sizes.lg;

  return (
    <div className={`relative ${s.container} ${className}`}>
      {/* Anneau violet animé - rotation + pulse */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: "conic-gradient(from 0deg, #8B5CF6, #A855F7, #D946EF, #EC4899, #F97316, #8B5CF6)",
        }}
        animate={{ 
          rotate: 360,
          scale: [1, 1.05, 1],
        }}
        transition={{
          rotate: {
            duration: 4,
            repeat: Infinity,
            ease: "linear",
          },
          scale: {
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }
        }}
      />
      
      {/* Fond blanc intérieur */}
      <div className="absolute inset-1 bg-white rounded-full" />
      
      {/* Logo centré */}
      <div className="absolute inset-0 flex items-center justify-center">
        <img 
          src={JYVIS_LOGO} 
          alt="JYVIS Assurances" 
          className={`${s.logo} object-contain`}
        />
      </div>
    </div>
  );
}
